import { gql, useQuery } from "@apollo/client";
import { Alert, Box, CircularProgress, MenuItem, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import UiSideModal from "components/base/UiSideModal";
import UiButton from "components/base/UiButton";
import { CostRow, Worksheet, materialUnits, money } from "./calculator";
import { plantMaterialRow } from "./library";
import WorksheetMaterialQuantity, { materialQuantityPreview } from "./WorksheetMaterialQuantity";
const PLANTS = gql`
  query WorksheetPlantLibrary($search: String, $page: Int!) {
    getPlants(search: $search, first: 20, page: $page) {
      data {
        id
        name
        products {
          id
          type
          rate
          plantId
        }
      }
      paginatorInfo {
        hasMorePages
      }
    }
  }
`;
type Plant = {
  id: string;
  name: string;
  products: { id: string; type: string; rate: number; plantId: string }[];
};
export default function WorksheetMaterialLibraryDrawer({
  initialRow,
  item,
  settings,
  disabled,
  onClose,
  onAdd,
}: {
  initialRow?: CostRow;
  item: Worksheet;
  settings: any;
  disabled: boolean;
  onClose: () => void;
  onAdd: (row: CostRow) => void;
}) {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [plantId, setPlantId] = useState(initialRow?.sourcePlantId ?? "");
  const [selectedPlant, setSelectedPlant] = useState<Plant | null>(
    initialRow
      ? {
          id: initialRow.sourcePlantId!,
          name: initialRow.sourcePlantName ?? "Saved plant",
          products: [],
        }
      : null
  );
  const [baseRow, setBaseRow] = useState<CostRow | null>(initialRow ?? null);
  const [productId, setProductId] = useState(initialRow?.sourceId ?? "");
  const [configuration, setConfiguration] = useState<Partial<CostRow>>({});
  const [unit, setUnit] = useState(initialRow?.unit ?? "");
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(search);
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);
  const { data, loading, error, refetch } = useQuery(PLANTS, {
    variables: { search: debounced, page },
    fetchPolicy: "network-only",
  });
  const pending = loading || debounced !== search;
  const plants: Plant[] = data?.getPlants?.data ?? [];
  const plant = plants.find((p) => p.id === plantId) ?? selectedPlant;
  const plantOptions =
    selectedPlant && !plants.some((p) => p.id === selectedPlant.id)
      ? [selectedPlant, ...plants]
      : plants;
  const products = plant?.products ?? [];
  const savedProduct =
    baseRow && baseRow.sourcePlantId === plantId && !products.some((p) => p.id === baseRow.sourceId)
      ? {
          id: baseRow.sourceId!,
          type: baseRow.sourceName ?? baseRow.name,
          rate: Number(baseRow.rate) * 100,
          plantId,
        }
      : null;
  const productOptions = savedProduct ? [savedProduct, ...products] : products;
  const product = productOptions.find((p) => p.id === productId);
  const draft = baseRow && product && unit ? { ...baseRow, ...configuration, unit } : null;
  const quantityError = draft ? materialQuantityPreview(item, draft).error : "";
  return (
    <UiSideModal
      open
      onClose={onClose}
      title={initialRow ? "Edit Plant Material" : "Add Material from Plant"}
      width="598px"
    >
      <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField
          size="small"
          label="Search plants"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
        />
        {error && (
          <Alert
            severity="error"
            action={
              <UiButton uiVariant="outlined-small" onClick={() => refetch()}>
                Retry
              </UiButton>
            }
          >
            {error.message}
          </Alert>
        )}
        {pending && <CircularProgress size={24} aria-label="Loading plants" />}
        {!pending && !error && !plants.length && <Typography>No matching plants.</Typography>}
        <TextField
          select
          size="small"
          label="Plant"
          value={plant?.id ?? ""}
          disabled={pending || disabled || !!error}
          onChange={(e) => {
            setPlantId(e.target.value);
            setSelectedPlant(plantOptions.find((p) => p.id === e.target.value) ?? null);
            setProductId("");
          }}
        >
          {plantOptions.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.name}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <UiButton
            uiVariant="outlined-small"
            disabled={pending || page === 1}
            onClick={() => {
              setPage(page - 1);
            }}
          >
            Previous
          </UiButton>
          <Typography variant="body2">Page {page}</Typography>
          <UiButton
            uiVariant="outlined-small"
            disabled={pending || !data?.getPlants?.paginatorInfo.hasMorePages}
            onClick={() => {
              setPage(page + 1);
            }}
          >
            Next
          </UiButton>
        </Box>
        {plant && !plant.products.length && (
          <Alert severity="info">This plant has no active products.</Alert>
        )}
        <TextField
          select
          size="small"
          label="Plant Product"
          value={product?.id ?? ""}
          disabled={!plant || pending || disabled}
          onChange={(e) => {
            setProductId(e.target.value);
            const next = productOptions.find((p) => p.id === e.target.value);
            if (plant && next) {
              const source = plantMaterialRow(plant, next, unit);
              setBaseRow((current) => ({
                ...current,
                ...source,
                quantity: current?.quantity ?? "1",
                calculation: current?.calculation ?? "manual",
              }));
            }
          }}
        >
          {productOptions.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.type} — {money(p.rate)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          required
          size="small"
          label="Purchasing unit"
          value={unit}
          disabled={disabled}
          onChange={(e) => setUnit(e.target.value)}
          helperText="Choose the unit this plant price purchases."
        >
          {materialUnits.map((u) => (
            <MenuItem key={u} value={u}>
              {u}
            </MenuItem>
          ))}
        </TextField>
        {product && (
          <Typography>
            Rate: {draft ? money(Number(draft.rate) * 100) : money(product.rate)}
            {unit ? ` per ${unit}` : ""}
          </Typography>
        )}
        {draft && (
          <WorksheetMaterialQuantity
            item={item}
            row={draft}
            settings={settings}
            busy={disabled}
            readOnly={false}
            onChange={(patch) => {
              if (patch.unit) setUnit(patch.unit);
              const { unit: changedUnit, ...rest } = patch;
              setConfiguration((current) => ({ ...current, ...rest }));
            }}
          />
        )}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          {initialRow && (
            <UiButton uiVariant="outlined-small" onClick={onClose}>
              Cancel
            </UiButton>
          )}
          <UiButton
            uiVariant="primary-green"
            disabled={!draft || pending || disabled || !!error || !!quantityError}
            onClick={() => {
              if (draft && !quantityError) {
                onAdd(draft);
                onClose();
              }
            }}
          >
            {initialRow ? "Apply" : "Add"}
          </UiButton>
        </Box>
      </Box>
    </UiSideModal>
  );
}
